import type { SceneDocument } from '../../types/scene';
import {
  parseLevelDesignImageAssetPath,
  persistLevelDesignImageAsset,
  readLevelDesignImageAsset,
} from '../levelDesignImageAssets';
import {
  createSceneImageAssetPath,
  parseSceneImageAssetPath,
  persistSceneImageAsset,
  readSceneImageAsset,
} from '../sceneImageAssets';
import {
  LEVEL_DESIGN_IMAGE_ASSET_COLLECTION,
  SCENE_IMAGE_ASSET_COLLECTION,
  markImageAssetDocumentMigrated,
  persistImageAssetDocument,
  readImageAssetDocument,
} from './imageAssetDocuments';
import { copyMediaAssetToStorageObjectPath } from './browserMedia';
import {
  createSceneAnimatedGifStorageObjectPath,
  createSceneBinaryAssetStorageObjectPath,
  inferImageContentType,
  inferImageExtension,
  isStorageObjectPath,
  sanitizeStorageAssetId,
  type SceneBinaryAssetSlot,
} from './mediaStoragePaths';
import { sanitizeAnimatedGifPlacements, toAnimatedGifPlacement } from './sceneAnimatedGifHelpers';

const resolveSceneAssetUrlString = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const assetId = parseSceneImageAssetPath(trimmed);
  if (!assetId) {
    return trimmed;
  }

  return readSceneImageAsset(assetId)?.trim() ?? '';
};

const resolveSceneAssetSourceAsync = async (value: string): Promise<string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const assetId = parseSceneImageAssetPath(trimmed);
  if (!assetId) {
    return trimmed;
  }

  const cached = readSceneImageAsset(assetId);
  if (cached) {
    return cached.trim();
  }

  const documentValue = await readImageAssetDocument(SCENE_IMAGE_ASSET_COLLECTION, assetId);
  if (!documentValue?.trim()) {
    return '';
  }

  persistSceneImageAsset(assetId, documentValue);
  return documentValue.trim();
};

const resolveLevelDesignAssetSourceAsync = async (value: string): Promise<string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const assetId = parseLevelDesignImageAssetPath(trimmed);
  if (!assetId) {
    return trimmed;
  }

  const cached = readLevelDesignImageAsset(assetId);
  if (cached) {
    return cached.trim();
  }

  const documentValue = await readImageAssetDocument(LEVEL_DESIGN_IMAGE_ASSET_COLLECTION, assetId);
  if (!documentValue?.trim()) {
    return '';
  }

  persistLevelDesignImageAsset(assetId, documentValue);
  return documentValue.trim();
};

const resolvePromotableSceneAssetSourceAsync = async (value: string): Promise<string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const sceneAssetId = parseSceneImageAssetPath(trimmed);
  if (sceneAssetId) {
    return resolveSceneAssetSourceAsync(trimmed);
  }

  const levelDesignAssetId = parseLevelDesignImageAssetPath(trimmed);
  if (levelDesignAssetId) {
    return resolveLevelDesignAssetSourceAsync(trimmed);
  }

  return trimmed;
};

const ensureSceneBinaryAssetStored = async (
  sceneId: string,
  slot: SceneBinaryAssetSlot,
  value: string | null,
): Promise<string | null> => {
  const original = value?.trim() ?? '';
  const resolved = original ? await resolveSceneAssetSourceAsync(original) : '';
  if (!resolved) {
    return null;
  }

  const sourceContentType = inferImageContentType(resolved);
  const targetExtension =
    slot === 'background' && sourceContentType === 'image/webp' ? 'webp' : 'png';
  const targetPath = createSceneBinaryAssetStorageObjectPath(sceneId, slot, targetExtension);
  if (resolved === targetPath) {
    return targetPath;
  }

  await copyMediaAssetToStorageObjectPath(resolved, targetPath, {
    contentType: targetExtension === 'webp' ? 'image/webp' : 'image/png',
  });

  const legacyAssetId = parseSceneImageAssetPath(original);
  if (legacyAssetId) {
    await markImageAssetDocumentMigrated(SCENE_IMAGE_ASSET_COLLECTION, legacyAssetId, targetPath);
  }

  return targetPath;
};

const ensureSceneAnimatedGifStored = async (
  sceneId: string,
  placement: NonNullable<SceneDocument['animatedGifs']>[number],
  index: number,
): Promise<NonNullable<SceneDocument['animatedGifs']>[number]> => {
  const original = placement.imagePath.trim();
  const resolved = original ? await resolveSceneAssetSourceAsync(original) : '';
  if (!resolved) {
    return placement;
  }

  const targetPath = createSceneAnimatedGifStorageObjectPath(
    sceneId,
    sanitizeStorageAssetId(placement.id, `animated-gif-${index + 1}`),
    inferImageExtension({
      fileName: resolved,
      contentType: inferImageContentType(resolved),
      fallback: 'gif',
    }),
  );

  if (!(isStorageObjectPath(resolved) && resolved === targetPath)) {
    await copyMediaAssetToStorageObjectPath(resolved, targetPath, {
      contentType: inferImageContentType(resolved) ?? 'image/gif',
    });
  }

  const legacyAssetId = parseSceneImageAssetPath(original);
  if (legacyAssetId) {
    await markImageAssetDocumentMigrated(SCENE_IMAGE_ASSET_COLLECTION, legacyAssetId, targetPath);
  }

  return {
    ...placement,
    imagePath: targetPath,
  };
};

export const resolveSceneAssetUrl = (value: unknown): string | null => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) {
    return null;
  }

  const resolved = resolveSceneAssetUrlString(raw);
  return resolved || null;
};

export const resolveSceneAssetUrlAsync = async (value: unknown): Promise<string | null> => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) {
    return null;
  }

  const resolved = await resolveSceneAssetSourceAsync(raw);
  return resolved || null;
};

export const toResolvedSceneAnimatedGifPlacement = (value: unknown, index: number) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  return toAnimatedGifPlacement(
    {
      ...entry,
      imagePath: resolveSceneAssetUrl(entry.imagePath) ?? '',
    },
    index,
  );
};

export const toResolvedSceneAnimatedGifPlacementAsync = async (value: unknown, index: number) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  return toAnimatedGifPlacement(
    {
      ...entry,
      imagePath: (await resolveSceneAssetUrlAsync(entry.imagePath)) ?? '',
    },
    index,
  );
};

export const externalizeSceneAssetUrls = async (
  sceneId: string,
  input: Pick<
    SceneDocument,
    'backgroundUrl' | 'foregroundUrl' | 'traversalMaskUrl' | 'thumbnailUrl' | 'animatedGifs'
  >,
): Promise<
  Pick<
    SceneDocument,
    'backgroundUrl' | 'foregroundUrl' | 'traversalMaskUrl' | 'thumbnailUrl' | 'animatedGifs'
  >
> => ({
  backgroundUrl: await ensureSceneBinaryAssetStored(sceneId, 'background', input.backgroundUrl),
  foregroundUrl: await ensureSceneBinaryAssetStored(sceneId, 'foreground', input.foregroundUrl),
  traversalMaskUrl: await ensureSceneBinaryAssetStored(
    sceneId,
    'traversal-mask',
    input.traversalMaskUrl,
  ),
  thumbnailUrl: await ensureSceneBinaryAssetStored(sceneId, 'thumbnail', input.thumbnailUrl),
  animatedGifs: await Promise.all(
    sanitizeAnimatedGifPlacements(input.animatedGifs).map((entry, index) =>
      ensureSceneAnimatedGifStored(sceneId, entry, index),
    ),
  ),
});

const promoteSceneBinaryAssetToSceneImageAsset = async (
  sceneId: string,
  ownerId: string,
  slot: SceneBinaryAssetSlot,
  value: string | null,
): Promise<string | null> => {
  const resolved = value?.trim() ? await resolvePromotableSceneAssetSourceAsync(value) : '';
  if (!resolved) {
    return null;
  }

  const assetId =
    slot === 'background'
      ? `${sceneId}:background`
      : slot === 'foreground'
        ? `${sceneId}:foreground`
        : slot === 'thumbnail'
          ? `${sceneId}:thumbnail`
          : `${sceneId}:traversal-mask`;
  persistSceneImageAsset(assetId, resolved);
  await persistImageAssetDocument(SCENE_IMAGE_ASSET_COLLECTION, assetId, resolved, {
    ownerId,
    sceneId,
  });
  return createSceneImageAssetPath(assetId);
};

const promoteSceneAnimatedGifToSceneImageAsset = async (
  sceneId: string,
  ownerId: string,
  placement: NonNullable<SceneDocument['animatedGifs']>[number],
  index: number,
): Promise<NonNullable<SceneDocument['animatedGifs']>[number]> => {
  const resolved = placement.imagePath.trim()
    ? await resolvePromotableSceneAssetSourceAsync(placement.imagePath)
    : '';
  if (!resolved) {
    return placement;
  }

  const assetId = `${sceneId}:animated-gif:${sanitizeStorageAssetId(
    placement.id,
    `animated-gif-${index + 1}`,
  )}`;
  persistSceneImageAsset(assetId, resolved);
  await persistImageAssetDocument(SCENE_IMAGE_ASSET_COLLECTION, assetId, resolved, {
    ownerId,
    sceneId,
  });
  return {
    ...placement,
    imagePath: createSceneImageAssetPath(assetId),
  };
};

export const promoteSceneAssetUrlsToSceneImageAssets = async (
  sceneId: string,
  ownerId: string,
  input: Pick<
    SceneDocument,
    'backgroundUrl' | 'foregroundUrl' | 'traversalMaskUrl' | 'thumbnailUrl' | 'animatedGifs'
  >,
): Promise<
  Pick<
    SceneDocument,
    'backgroundUrl' | 'foregroundUrl' | 'traversalMaskUrl' | 'thumbnailUrl' | 'animatedGifs'
  >
> => ({
  backgroundUrl: await promoteSceneBinaryAssetToSceneImageAsset(
    sceneId,
    ownerId,
    'background',
    input.backgroundUrl,
  ),
  foregroundUrl: await promoteSceneBinaryAssetToSceneImageAsset(
    sceneId,
    ownerId,
    'foreground',
    input.foregroundUrl,
  ),
  traversalMaskUrl: await promoteSceneBinaryAssetToSceneImageAsset(
    sceneId,
    ownerId,
    'traversal-mask',
    input.traversalMaskUrl,
  ),
  thumbnailUrl: await promoteSceneBinaryAssetToSceneImageAsset(
    sceneId,
    ownerId,
    'thumbnail',
    input.thumbnailUrl,
  ),
  animatedGifs: await Promise.all(
    sanitizeAnimatedGifPlacements(input.animatedGifs).map((entry, index) =>
      promoteSceneAnimatedGifToSceneImageAsset(sceneId, ownerId, entry, index),
    ),
  ),
});
